import type { SolarProposalAdderDto } from "~/api/dto/solar-proposal-pricing-steps.dto"
import useSolarProposalAdders from "~/api/hooks/useSolarProposalAdders"
import useSolarProposalExtendedWarranties from "~/api/hooks/useSolarProposalExtendedWarranties"
import useSolarProposalIncentives from "~/api/hooks/useSolarProposalIncentives"
import useSolarProposalPricing from "~/api/hooks/useSolarProposalPricing"
import useSolarProposalProductOffering from "~/api/hooks/useSolarProposalProductOffering"
import useSolarProposalVppPrograms from "~/api/hooks/useSolarProposalVppPrograms"
import useUpdateSolarProposalAdders from "~/api/hooks/useUpdateSolarProposalAdders"
import useUpdateSolarProposalExtendedWarranty from "~/api/hooks/useUpdateSolarProposalExtendedWarranty"
import useUpdateSolarProposalIncentives from "~/api/hooks/useUpdateSolarProposalIncentives"
import useUpdateSolarProposalVppPrograms from "~/api/hooks/useUpdateSolarProposalVppPrograms"
import {
  SOLAR_SYSTEM_PRICING_REQUIRED_CODE,
  solarProposalErrorCode,
} from "~/api/solar-proposal-errors"

import { SOLAR_CREDIT_HUMAN_FINANCE_TYPE } from "./solar-finance-partner"
import { solarSetupStepPath } from "./solar-setup-paths"
import { SolarIncentivesRefresh } from "./SolarIncentivesRefresh"
import { SolarLockedNote } from "./SolarLockedNote"
import { useSolarRebatesCompanion } from "./SolarRebatesSection"
import { describeCommandError } from "../ProposalSetup/pricing/describe-command-error"
import {
  pricingCommand,
  type AdderCatalog,
  type AddersRead,
  type PricingAdder,
  type PricingFinancingLock,
  type PricingKindBundle,
  type PricingPayeeOption,
  type PricingStatusRead,
} from "../ProposalSetup/pricing/pricing-kind-bundle"
import { VPP_SAVE_FAILED } from "../ProposalSetup/pricing/PricingVppProgramsStep"

export const SOLAR_VPP_NO_ELIGIBLE_PROGRAMS = `No VPP programs are eligible for this proposal.`
export const SOLAR_VPP_NO_ELIGIBLE_PROGRAMS_DETAIL = `Eligibility depends on the property's state, the system's equipment and the Finance Plan. Change any of those and check back.`
export const SOLAR_NO_ELIGIBLE_INCENTIVES = `No eligible incentives for this home and system`

const SOLAR_FINANCING_LOCKED_FALLBACK = `Financing locks pricing inputs.`

const toPricingAdder = (adder: SolarProposalAdderDto): PricingAdder => ({
  id: adder.powurOsAdderId,
  title: adder.title,
  category: adder.category,
  required: adder.isRequired,
  listed: true,
  quantityMode: adder.quantityMode,
  unitLabel: adder.unitLabel,
  listPrice: adder.price,
  derivedQuantity: adder.derivedQuantity,
  // The server prices fixed and derived adders itself and ignores a quantity.
  defaultQuantity: null,
})

const adderCatalog = (adders: SolarProposalAdderDto[]): AdderCatalog => ({
  adders: adders.map(toPricingAdder),
  categories: [
    ...new Map(
      adders.flatMap((adder) =>
        adder.category ? [[adder.category.id, adder.category] as const] : [],
      ),
    ).values(),
  ].map(({ id, name }) => ({ id, name })),
  saved: Object.fromEntries(
    adders.map((adder) => {
      const selected = adder.isRequired || adder.selected
      return [
        adder.powurOsAdderId,
        {
          selected,
          customQuantity: selected ? adder.customQuantity : null,
          customPrice: selected ? adder.customPrice : null,
        },
      ]
    }),
  ),
})

const useSolarAdders = (proposalId: string): AddersRead => {
  const query = useSolarProposalAdders(proposalId)
  return {
    loading: query.isLoading,
    // A 409 says why (no utility chosen yet).
    loadError: query.isError
      ? describeCommandError(query.error, `Unable to load available adders`)
      : null,
    retry: () => {
      void query.refetch()
    },
    catalog: query.isSuccess ? adderCatalog(query.data.adders) : null,
    ready: query.isSuccess,
  }
}

/**
 * The Product Offering read's financing lock, explained with Solar's lock
 * note. Writes hold until it loads; a failed read leaves the server's 409 to
 * refuse a locked write.
 */
const useSolarPricingFinancingLock = (
  proposalId: string,
): PricingFinancingLock => {
  const query = useSolarProposalProductOffering(proposalId)
  const locked = query.data?.financingLocked === true
  return {
    locked,
    pending: query.data == null && !query.isError,
    notice: locked ? (
      <div className="mb-4">
        <SolarLockedNote testId="solar-financing-locked">
          {query.data?.financingLockReason ?? SOLAR_FINANCING_LOCKED_FALLBACK}
        </SolarLockedNote>
      </div>
    ) : null,
  }
}

const CASH_PAYEE_OPTIONS: readonly PricingPayeeOption[] = [
  { value: `homeowner`, label: `Homeowner receives it`, note: null },
  { value: `powur`, label: `Applied to the system price (Powur)`, note: null },
]

const CREDIT_HUMAN_PAYEE_OPTIONS: readonly PricingPayeeOption[] = [
  { value: `homeowner`, label: `Homeowner receives it`, note: null },
  { value: `powur`, label: `Apply to the loan`, note: null },
]

/** A Powur-paid incentive goes to the loan on Credit Human, to the system price on Cash. */
const useSolarPayeeOptions = (
  proposalId: string,
): readonly PricingPayeeOption[] =>
  useSolarProposalProductOffering(proposalId).data?.partner.financeType ===
  SOLAR_CREDIT_HUMAN_FINANCE_TYPE
    ? CREDIT_HUMAN_PAYEE_OPTIONS
    : CASH_PAYEE_OPTIONS

const useSolarPricingStatus = (proposalId: string): PricingStatusRead => {
  const query = useSolarProposalPricing(proposalId)
  return {
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    hasPricing:
      query.data != null && query.data.pricingStatus !== `not_calculated`,
    refetch: query.refetch,
  }
}

/** Solar Proposal reads and commands for the shared pricing steps. */
export const solarPricingBundle: PricingKindBundle = {
  systemPricingPath: (proposalId) =>
    solarSetupStepPath(proposalId, `system-pricing`),
  useFinancingLock: useSolarPricingFinancingLock,
  usePricingStatus: useSolarPricingStatus,
  adders: {
    copy: { saveLabel: `Save` },
    useAdders: useSolarAdders,
    useSave: (proposalId) =>
      pricingCommand(useUpdateSolarProposalAdders(), (body) => ({
        proposalId,
        body: { adders: [...body.adders] },
      })),
    describeSaveError: (error) =>
      describeCommandError(error, `Unable to save adders`),
    useCompanion: useSolarRebatesCompanion,
  },
  warranty: {
    copy: {
      title: `Protection Plans`,
      loading: `Loading Protection Plans`,
      empty: `No Protection Plans available`,
      loadFailed: `Unable to load Protection Plans`,
    },
    useCatalog: useSolarProposalExtendedWarranties,
    // The read itself carries the saved snapshot when PowurOS fails
    // (`catalogUnavailable`); a failed read has nothing else to offer.
    useSavedWhenCatalogFails: () => null,
    useSave: (proposalId) =>
      pricingCommand(
        useUpdateSolarProposalExtendedWarranty(),
        (warrantyId: number | null) => ({ proposalId, body: { warrantyId } }),
      ),
  },
  incentives: {
    copy: {
      empty: SOLAR_NO_ELIGIBLE_INCENTIVES,
      pricingRequiredDetail: `Incentive values are calculated from the stored System Pricing. Calculate pricing first, then come back to pick incentives.`,
    },
    usePayeeOptions: useSolarPayeeOptions,
    useIncentives: useSolarProposalIncentives,
    useSave: (proposalId) =>
      pricingCommand(useUpdateSolarProposalIncentives(), (body) => ({
        proposalId,
        body,
      })),
    describeSaveError: (error) =>
      describeCommandError(
        error,
        `Unable to save incentives. Your changes are still here.`,
      ),
    isPricingRequiredError: (error) =>
      solarProposalErrorCode(error) === SOLAR_SYSTEM_PRICING_REQUIRED_CODE,
    Toolbar: SolarIncentivesRefresh,
  },
  vpp: {
    copy: {
      empty: SOLAR_VPP_NO_ELIGIBLE_PROGRAMS,
      emptyDetail: SOLAR_VPP_NO_ELIGIBLE_PROGRAMS_DETAIL,
    },
    showRemovedPrograms: true,
    usePrograms: useSolarProposalVppPrograms,
    useSave: (proposalId) =>
      pricingCommand(useUpdateSolarProposalVppPrograms(), (body) => ({
        proposalId,
        body,
      })),
    describeSaveError: (error) => describeCommandError(error, VPP_SAVE_FAILED),
  },
}
